import { isProd } from '@/utils/env';
import { getNetworkType } from '@/utils/networkUtils';
import { loadScript } from '@/utils';
import { getCountry } from '@/utils/locale';

/**
 * 可观测 RUM: 采集意料之外的错误.
 * 对于预期内但需留痕的错误, 使用 dropErrors 过滤上报, 并通过 recordLog 记录详情.
 */
const ALLOWED_ERROR_PATTERNS = [
    // 非锁屏环境, 获取不到 JSBridge 的报错
    // Ⅰ. 包含 "未找到 appJsBridge" 的报错
    /未找到 appJsBridge/,

    // 第三方脚本报错
    // Ⅰ. 等于 "Script error." 的报错
    /^Script error\.$/,

    // 网络原因导致请求失败
    // Ⅰ. 形如 "Document error, xpath: //..., src: https://..." 的报错 (... 表示任意字符)
    /^Document error, xpath: \/\/.*, src: https:\/\/.*$/,
    // Ⅱ. 形如 "Document error, xpath: //..., href: https://..." 的报错 (... 表示任意字符)
    /^Document error, xpath: \/\/.*, href: https:\/\/.*$/,
    // Ⅲ. 等于 "Failed to fetch" 的报错
    /^Failed to fetch$/,
    // Ⅳ. 等于 "SendBeacon failed" 的报错
    /^SendBeacon failed$/,

    // jssdk 的报错
    // Ⅰ. 包含 "ads-jssdk-oversea" 的报错
    /ads-jssdk-oversea/,

    // google sdk 报错
    // Ⅰ. 等于 "adsbygoogle.push() error: All 'ins' elements in the DOM with class=adsbygoogle already have ads in them." 的报错
    // 该报错说明所有广告坑位都已加载好广告
    /^adsbygoogle\.push\(\) error: All 'ins' elements in the DOM with class=adsbygoogle already have ads in them\.$/,
    // Ⅱ. 等于 "no_div" 的报错
    // 该报错说明页面没有广告坑位
    /^no_div$/,
    // Ⅲ. 等于 "carr" 的报错
    // 该报错说明广告请求失败或广告资源加载异常
    /^carr$/,

    // ga4 报错
    // Ⅰ. 包含 'googletag' 的报错
    /googletag/,
];

/**
 * 错误过滤器: 判断错误是否应该丢弃
 * @param name - 错误名称
 * @param msg - 错误消息
 * @param stack - 错误堆栈
 * @returns true 表示丢弃该错误 (不上报), false 表示保留 (正常上报)
 * @description 命中白名单的错误会被丢弃, 但仍会通过 recordLog 记录日志
 */
const dropErrors = (name: string, msg: string, stack: string) => {
    const isAllowed = ALLOWED_ERROR_PATTERNS.some((re) => re.test(msg));

    const logInfo = { func: 'dropErrors', name, msg, stack, isAllowed };
    const logStr = JSON.stringify(logInfo);

    if (isAllowed && window.cloudObservationRum) {
        window.cloudObservationRum.recordLog('WARN', logStr);
    }
    console.warn({ logStr, logInfo });

    return isAllowed;
};

/**
 * 按用户国家映射到数据采集节点
 * @returns RUM 数据上报节点 ('eu-par' | 'india' | 'sgp')
 */
const getRumRegion = () => {
    return 'sgp';
};

/**
 * 初始化 RUM SDK
 */
function initializeRum() {
    if (!window.cloudObservationRum) {
        console.warn('cloudObservationRum not found');
        return;
    }

    const collectionSourceUrl = isProd
        ? `https://ums-telemetry-${getRumRegion()}.heytapmobi.com`
        : 'https://ums-telemetry-cn.heytapmobi.com';

    window.cloudObservationRum.initialize({
        collectionSourceUrl,
        serviceName: 'lockscreen-detail-page',
        moduleName: 'home-page',
        deploymentVersion: 'home-page',
        deploymentEnvironment: getCountry(),
        dropErrors,
        scopeAttributes: {
            // 网络信息
            cus_network: getNetworkType(),
            // 是否使用了离线包
            cus_offline: `${!!window.offline}`,
        },
    });
}

/**
 * 加载并初始化 RUM SDK
 */
export function initCloudObservation() {
    loadScript({
        src: 'https://ums-rum-global.heytapimage.com/2.6.0/cloud-observation-rum.global.prod.js',
        scriptId: 'cloud-observation-rum',
        async: true,
        crossOrigin: 'anonymous',
    }).then(() => {
        console.log('cloud-observation-rum loaded', window.cloudObservationRum);
        initializeRum();
    });
}
